import { useState, useEffect, useMemo, useCallback } from 'react';
import HelpInfo from '@/components/HelpInfo';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTheme } from '@/contexts/ThemeContext';
import { PermissionGate, useRBAC } from '@/contexts/RBACContext';
import { formatDate, formatRelativeTime, formatDateTime } from '@/utils/localeFormatters';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import EmailComposer from '@/components/EmailComposer';
import { 
  Plus, Mail, Phone, Building, Sparkles, TrendingUp, TrendingDown, 
  ChevronRight, Clock, Search, Filter, SortAsc, SortDesc, Calendar,
  X, RefreshCw, Users, LayoutGrid, List, ExternalLink, Kanban,
  Edit, PhoneCall, MoreHorizontal, UserCircle, CheckSquare, CalendarDays,
  Trash2, AlertTriangle, ArrowUpDown, ArrowUp, ArrowDown, MessageCircle, Download,
  Command, MessageSquare, UserPlus, Bell, Archive, RotateCcw, Ban, Star,
  Inbox, ClipboardList, UserCheck, AlertCircle, Target
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Lead category definitions with dark mode support
const LEAD_CATEGORIES = {
  all: {
    id: 'all',
    label: 'All Leads',
    icon: Users,
    color: 'slate',
    bgColor: { light: 'bg-slate-800', dark: 'bg-slate-800' },
    textColor: { light: 'text-white', dark: 'text-white' },
    borderColor: { light: 'border-slate-700', dark: 'border-slate-600' },
    iconBg: { light: 'bg-slate-700', dark: 'bg-slate-700' },
    description: 'View all leads across all categories'
  },
  new: {
    id: 'new',
    label: 'New Leads',
    icon: Inbox,
    color: 'blue',
    bgColor: { light: 'bg-blue-50', dark: 'bg-blue-900/30' },
    textColor: { light: 'text-blue-700', dark: 'text-blue-300' },
    borderColor: { light: 'border-blue-200', dark: 'border-blue-700' },
    iconBg: { light: 'bg-blue-100', dark: 'bg-blue-800/50' },
    description: 'Newly captured and unassigned leads'
  },
  followup: {
    id: 'followup',
    label: 'Follow-up',
    icon: ClipboardList,
    color: 'amber',
    bgColor: { light: 'bg-amber-50', dark: 'bg-amber-900/30' },
    textColor: { light: 'text-amber-700', dark: 'text-amber-300' },
    borderColor: { light: 'border-amber-200', dark: 'border-amber-700' },
    iconBg: { light: 'bg-amber-100', dark: 'bg-amber-800/50' },
    description: 'Leads requiring further action'
  },
  junk: {
    id: 'junk',
    label: 'Junk',
    icon: Ban,
    color: 'rose',
    bgColor: { light: 'bg-rose-50', dark: 'bg-rose-900/30' },
    textColor: { light: 'text-rose-700', dark: 'text-rose-300' },
    borderColor: { light: 'border-rose-300', dark: 'border-rose-700' },
    iconBg: { light: 'bg-rose-100', dark: 'bg-rose-800/50' },
    description: 'Invalid or irrelevant leads'
  },
  assigned: {
    id: 'assigned',
    label: 'Assigned',
    icon: UserCheck,
    color: 'emerald',
    bgColor: { light: 'bg-emerald-50', dark: 'bg-emerald-900/30' },
    textColor: { light: 'text-emerald-700', dark: 'text-emerald-300' },
    borderColor: { light: 'border-emerald-200', dark: 'border-emerald-700' },
    iconBg: { light: 'bg-emerald-100', dark: 'bg-emerald-800/50' },
    description: 'Leads assigned to team members'
  },
  utm_tracker: {
    id: 'utm_tracker',
    label: 'UTM Tracker',
    icon: Target,
    color: 'violet',
    bgColor: { light: 'bg-violet-50', dark: 'bg-violet-900/30' },
    textColor: { light: 'text-violet-700', dark: 'text-violet-300' },
    borderColor: { light: 'border-violet-200', dark: 'border-violet-700' },
    iconBg: { light: 'bg-violet-100', dark: 'bg-violet-800/50' },
    description: 'Campaign attribution & UTM tracking'
  }
};

// Junk reasons
const JUNK_REASONS = [
  { id: 'spam', label: 'Spam', icon: '🚫' },
  { id: 'incorrect_info', label: 'Incorrect Information', icon: '❌' },
  { id: 'not_interested', label: 'Not Interested', icon: '👎' },
  { id: 'duplicate', label: 'Duplicate', icon: '📋' },
  { id: 'invalid_contact', label: 'Invalid Contact', icon: '⚠️' },
  { id: 'other', label: 'Other', icon: '📝' }
];

// Priority styles (theme-aware)
const getPriorityStyles = (isDark) => ({
  high: { 
    bg: isDark ? 'bg-red-900/40' : 'bg-red-100', 
    text: isDark ? 'text-red-300' : 'text-red-700', 
    dot: 'bg-red-500' 
  },
  medium: { 
    bg: isDark ? 'bg-amber-900/40' : 'bg-amber-100', 
    text: isDark ? 'text-amber-300' : 'text-amber-700', 
    dot: 'bg-amber-500' 
  },
  normal: { 
    bg: isDark ? 'bg-slate-800' : 'bg-slate-100', 
    text: isDark ? 'text-slate-300' : 'text-slate-600', 
    dot: 'bg-slate-400' 
  },
  low: { 
    bg: isDark ? 'bg-slate-800/50' : 'bg-slate-50', 
    text: isDark ? 'text-slate-400' : 'text-slate-500', 
    dot: 'bg-slate-300' 
  }
});

// Status badge styles (theme-aware)
const getStatusStyles = (isDark) => ({
  new: { bg: isDark ? 'bg-blue-900/40' : 'bg-blue-50', text: isDark ? 'text-blue-300' : 'text-blue-700', border: isDark ? 'border-blue-700' : 'border-blue-200' },
  lead: { bg: isDark ? 'bg-indigo-900/40' : 'bg-indigo-50', text: isDark ? 'text-indigo-300' : 'text-indigo-700', border: isDark ? 'border-indigo-700' : 'border-indigo-200' },
  contacted: { bg: isDark ? 'bg-violet-900/40' : 'bg-violet-50', text: isDark ? 'text-violet-300' : 'text-violet-700', border: isDark ? 'border-violet-700' : 'border-violet-200' },
  qualified: { bg: isDark ? 'bg-amber-900/40' : 'bg-amber-50', text: isDark ? 'text-amber-300' : 'text-amber-700', border: isDark ? 'border-amber-700' : 'border-amber-200' },
  opportunity: { bg: isDark ? 'bg-orange-900/40' : 'bg-orange-50', text: isDark ? 'text-orange-300' : 'text-orange-700', border: isDark ? 'border-orange-700' : 'border-orange-200' },
  customer: { bg: isDark ? 'bg-emerald-900/40' : 'bg-emerald-50', text: isDark ? 'text-emerald-300' : 'text-emerald-700', border: isDark ? 'border-emerald-700' : 'border-emerald-200' },
  meeting_scheduled: { bg: isDark ? 'bg-cyan-900/40' : 'bg-cyan-50', text: isDark ? 'text-cyan-300' : 'text-cyan-700', border: isDark ? 'border-cyan-700' : 'border-cyan-200' },
  junk: { bg: isDark ? 'bg-slate-800' : 'bg-slate-100', text: isDark ? 'text-slate-300' : 'text-slate-600', border: isDark ? 'border-slate-700' : 'border-slate-300' },
  not_interested: { bg: isDark ? 'bg-slate-800' : 'bg-slate-100', text: isDark ? 'text-slate-300' : 'text-slate-600', border: isDark ? 'border-slate-700' : 'border-slate-200' },
  lost: { bg: isDark ? 'bg-red-900/40' : 'bg-red-50', text: isDark ? 'text-red-300' : 'text-red-600', border: isDark ? 'border-red-700' : 'border-red-200' },
});

const LeadDashboard = () => {
  const { token, user } = useAuth();
  const { t } = useLanguage();
  const { isDarkMode } = useTheme();
  const { hasPermission } = useRBAC();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Check if user can assign leads - only Kheldoune or Admin
  const allowedAssignmentEmails = [
    'z.kheldoune@fakhruddinproperties.com',
    'admin@treppanliving.ae'
  ];
  const canAssignLeads = user?.email && (
    allowedAssignmentEmails.includes(user.email.toLowerCase()) ||
    ['super_admin', 'admin', 'owner', 'account_owner'].includes(user.role?.toLowerCase().replace(' ', '_'))
  );

  // Main state
  const [activeCategory, setActiveCategory] = useState('all');
  const [contacts, setContacts] = useState([]);
  const [contactsFetchedAt, setContactsFetchedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [categoryCounts, setCategoryCounts] = useState({ all: 0, new: 0, followup: 0, junk: 0, assigned: 0, utm_tracker: 0 });
  
  // UTM Tracker specific state
  const [utmStats, setUtmStats] = useState({ fully_attributed: 0, partially_attributed: 0, unattributed: 0, total: 0 });
  const [utmSourceFilter, setUtmSourceFilter] = useState('all');
  const [utmMediumFilter, setUtmMediumFilter] = useState('all');
  const [utmCampaignFilter, setUtmCampaignFilter] = useState('all');
  const [attributionFilter, setAttributionFilter] = useState('all');
  
  // Filter states. status / owner / lifecycle / source initialise
  // straight from the URL (?status=&owner_id=&lifecycle_stage=&source=)
  // so a deep-link — e.g. clicking a status bar on the agent dashboard
  // — lands pre-filtered on the FIRST render. Doing this via a useEffect
  // instead caused a race: the page fetched unfiltered first, then
  // re-fetched filtered, and whichever resolved last won — so the view
  // would flash filtered then revert to all.
  const [searchQuery, setSearchQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState(() => searchParams.get('source') || 'all');
  const [ownerFilter, setOwnerFilter] = useState(() => searchParams.get('owner_id') || 'all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get('status') || 'all');
  const [lifecycleStageFilter, setLifecycleStageFilter] = useState(() => searchParams.get('lifecycle_stage') || 'all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [assignedFrom, setAssignedFrom] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [showFilters, setShowFilters] = useState(
    () => !!(searchParams.get('status') || searchParams.get('owner_id')
      || searchParams.get('lifecycle_stage') || searchParams.get('source')),
  );
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [quickDateFilter, setQuickDateFilter] = useState('all');


  // Quick date filter options
  const quickDateOptions = [
    { value: 'all', label: 'All Time' },
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'this_week', label: 'This Week' },
    { value: 'last_week', label: 'Last Week' },
    { value: 'this_month', label: 'This Month' },
    { value: 'last_month', label: 'Last Month' },
    { value: 'this_year', label: 'This Year' },
    { value: 'custom', label: 'Custom Range' },
  ];
  
  // Helper to format date as YYYY-MM-DD without timezone conversion
  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  // Helper to calculate date range based on quick filter
  const getDateRange = (filter) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch (filter) {
      case 'today':
        return { from: formatDateLocal(today), to: formatDateLocal(today) };
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        return { from: formatDateLocal(yesterday), to: formatDateLocal(yesterday) };
      case 'this_week':
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        return { from: formatDateLocal(startOfWeek), to: formatDateLocal(today) };
      case 'last_week':
        const lastWeekStart = new Date(today);
        lastWeekStart.setDate(today.getDate() - today.getDay() - 7);
        const lastWeekEnd = new Date(lastWeekStart);
        lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
        return { from: formatDateLocal(lastWeekStart), to: formatDateLocal(lastWeekEnd) };
      case 'this_month':
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        return { from: formatDateLocal(startOfMonth), to: formatDateLocal(today) };
      case 'last_month':
        const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
        return { from: formatDateLocal(lastMonthStart), to: formatDateLocal(lastMonthEnd) };
      case 'this_year':
        const startOfYear = new Date(now.getFullYear(), 0, 1);
        return { from: formatDateLocal(startOfYear), to: formatDateLocal(today) };
      default:
        return { from: '', to: '' };
    }
  };
  
  // Handle quick date filter change
  const handleQuickDateChange = (value) => {
    setQuickDateFilter(value);
    if (value !== 'custom' && value !== 'all') {
      const range = getDateRange(value);
      setDateFrom(range.from);
      setDateTo(range.to);
    } else if (value === 'all') {
      setDateFrom('');
      setDateTo('');
    }
  };
  
  // Filter options
  const [filterOptions, setFilterOptions] = useState({ sources: [], statuses: [], owners: [] });
  const [allUsers, setAllUsers] = useState([]);
  
  // Lifecycle stage options
  const lifecycleStages = [
    { value: 'all', label: 'All Stages' },
    { value: 'lead', label: 'Lead' },
    { value: 'mql', label: 'MQL (Marketing Qualified)' },
    { value: 'sql', label: 'SQL (Sales Qualified)' },
    { value: 'opportunity', label: 'Opportunity' },
    { value: 'customer', label: 'Customer' },
    { value: 'evangelist', label: 'Evangelist' },
    { value: 'churned', label: 'Churned' },
  ];
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalContacts, setTotalContacts] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(25);
  
  // Selection states
  const [selectedContacts, setSelectedContacts] = useState([]);
  
  // Dialog states
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
  const [isJunkDialogOpen, setIsJunkDialogOpen] = useState(false);
  const [isFollowupDialogOpen, setIsFollowupDialogOpen] = useState(false);
  const [isRestoreDialogOpen, setIsRestoreDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isAddLeadDialogOpen, setIsAddLeadDialogOpen] = useState(false);
  const [newLead, setNewLead] = useState({ name: '', email: '', phone: '', company: '', source: 'Website' });
  const [contactForAction, setContactForAction] = useState(null);
  const [selectedJunkReason, setSelectedJunkReason] = useState('');
  const [junkNotes, setJunkNotes] = useState('');
  const [followupDate, setFollowupDate] = useState('');
  const [followupNotes, setFollowupNotes] = useState('');
  const [selectedAssignee, setSelectedAssignee] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Email composer
  const [emailContact, setEmailContact] = useState(null);
  const [isEmailComposerOpen, setIsEmailComposerOpen] = useState(false);
  
  // Export state
  const [isExporting, setIsExporting] = useState(false);

  // Handle /contacts/new route - open add lead dialog
  useEffect(() => {
    if (location.pathname === '/contacts/new') {
      setIsAddLeadDialogOpen(true);
    }
  }, [location.pathname]);

  // Handle Add Lead
  const handleAddLead = async () => {
    if (!newLead.name.trim()) {
      toast.error('Name is required');
      return;
    }
    try {
      const response = await axios.post(`${API}/contacts?token=${token}`, {
        name: newLead.name,
        email: newLead.email || null,
        phone: newLead.phone || null,
        company: newLead.company || null,
        source: newLead.source || 'Website'
      });
      
      if (response.data?.id) {
        toast.success('Lead created successfully');
        setIsAddLeadDialogOpen(false);
        setNewLead({ name: '', email: '', phone: '', company: '', source: 'Website' });
        // Navigate to the new contact
        navigate(`/contacts/${response.data.id}`);
      }
    } catch (error) {
      console.error('Add lead error:', error);
      toast.error(error.response?.data?.error || 'Failed to create lead');
    }
  };

  // Export to Excel function
  const handleExportToExcel = async () => {
    try {
      setIsExporting(true);
      const response = await axios.get(`${API}/contacts/export?token=${token}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `leads_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export completed successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export leads');
    } finally {
      setIsExporting(false);
    }
  };

  // Determine query filter based on active category
  const getCategoryQuery = useCallback(() => {
    // Note: The backend RBAC automatically handles role-based filtering
    // - For admins: "new" shows unassigned leads, "assigned" shows all assigned
    // - For agents: "new" shows their unviewed leads, "assigned" shows their viewed leads
    // DO NOT add ownership_filter here as it overrides the backend RBAC logic
    switch (activeCategory) {
      case 'all':
        return {}; // No category filter - fetch all leads
      case 'new':
        return { lead_category: 'new' };
      case 'followup':
        return { lead_category: 'followup' }; // For agents: viewed leads needing follow-up
      case 'junk':
        return { lead_category: 'junk', status: 'junk' };
      case 'assigned':
        return { lead_category: 'assigned' };
      case 'utm_tracker':
        return { utm_tracker: 'true' }; // Special flag for UTM tracker view
      default:
        return {};
    }
  }, [activeCategory]);

  // Fetch contacts based on category
  const fetchContacts = useCallback(async () => {
    try {
      setLoading(true);
      const categoryQuery = getCategoryQuery();
      
      // Special handling for UTM Tracker view
      if (activeCategory === 'utm_tracker') {
        const params = new URLSearchParams({
          token,
          page: currentPage.toString(),
          limit: pageSize.toString(),
          sort_by: sortBy,
          sort_order: sortOrder
        });
        
        if (attributionFilter !== 'all') params.append('attribution_status', attributionFilter);
        if (utmSourceFilter !== 'all') params.append('utm_source', utmSourceFilter);
        if (utmMediumFilter !== 'all') params.append('utm_medium', utmMediumFilter);
        if (utmCampaignFilter !== 'all') params.append('utm_campaign', utmCampaignFilter);
        if (searchQuery) params.append('search', searchQuery);
        
        const response = await axios.get(`${API}/contacts/utm-tracker?${params}`);
        
        if (response.data.contacts) {
          setContacts(response.data.contacts);
          setTotalContacts(response.data.total || response.data.contacts.length);
          setTotalPages(response.data.pages || Math.ceil((response.data.total || response.data.contacts.length) / pageSize));
          if (response.data.attribution_summary) {
            setUtmStats(response.data.attribution_summary);
          }
        }
        setLoading(false);
        return;
      }
      
      const params = new URLSearchParams({
        token,
        page: currentPage.toString(),
        limit: pageSize.toString(),
        sort_by: sortBy,
        sort_order: sortOrder,
        ...categoryQuery
      });

      if (searchQuery) params.append('search', searchQuery);
      if (sourceFilter !== 'all') params.append('source', sourceFilter);
      if (ownerFilter !== 'all') params.append('owner_id', ownerFilter);
      if (priorityFilter !== 'all') params.append('priority', priorityFilter);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (lifecycleStageFilter !== 'all') params.append('lifecycle_stage', lifecycleStageFilter);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (assignedFrom) params.append('assigned_from', assignedFrom);
      if (assignedTo) params.append('assigned_to', assignedTo);

      const response = await axios.get(`${API}/contacts?${params}`);

      if (response.data.contacts) {
        setContacts(response.data.contacts);
        setTotalContacts(response.data.total || response.data.contacts.length);
        setTotalPages(response.data.pages || Math.ceil((response.data.total || response.data.contacts.length) / pageSize));
      } else if (Array.isArray(response.data)) {
        setContacts(response.data);
        setTotalContacts(response.data.length);
        setTotalPages(1);
      }
      setContactsFetchedAt(new Date());
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [token, currentPage, pageSize, sortBy, sortOrder, activeCategory, searchQuery, sourceFilter, ownerFilter, priorityFilter, statusFilter, lifecycleStageFilter, dateFrom, dateTo, assignedFrom, assignedTo, getCategoryQuery, attributionFilter, utmSourceFilter, utmMediumFilter, utmCampaignFilter]);

  // Fetch category counts
  const fetchCategoryCounts = useCallback(async () => {
    try {
      // Note: The backend RBAC automatically filters based on user role
      // - For admins: "new" = unassigned leads, "assigned" = all assigned leads
      // - For agents: "new" = their unviewed leads, "followup" = their viewed leads
      const [allRes, newRes, followupRes, junkRes, assignedRes] = await Promise.all([
        axios.get(`${API}/contacts?token=${token}&limit=1`),
        axios.get(`${API}/contacts?token=${token}&limit=1&lead_category=new`),
        axios.get(`${API}/contacts?token=${token}&limit=1&lead_category=followup`),
        axios.get(`${API}/contacts?token=${token}&limit=1&lead_category=junk&status=junk`),
        axios.get(`${API}/contacts?token=${token}&limit=1&lead_category=assigned`)
      ]);
      
      setCategoryCounts({
        all: allRes.data.total || 0,
        new: newRes.data.total || 0,
        followup: followupRes.data.total || 0,
        junk: junkRes.data.total || 0,
        assigned: assignedRes.data.total || 0
      });
    } catch (error) {
      console.error('Error fetching category counts:', error);
    }
  }, [token]);

  // Fetch filter options
  const fetchFilterOptions = async () => {
    try {
      const response = await axios.get(`${API}/contacts/filters?token=${token}`);
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  // Fetch all users for assignment
  const fetchAllUsers = async () => {
    try {
      const response = await axios.get(`${API}/accounts/users?token=${token}`);
      setAllUsers(response.data?.users || response.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      setAllUsers([]);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchFilterOptions();
    fetchAllUsers();
    fetchCategoryCounts();
  }, [token, fetchCategoryCounts]);

  // Fetch contacts when filters change
  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  // Debounced search
  useEffect(() => {
    const debounce = setTimeout(() => {
      setCurrentPage(1);
      fetchContacts();
    }, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery]);

  // Reset page when category changes
  useEffect(() => {
    setCurrentPage(1);
    setSelectedContacts([]);
  }, [activeCategory]);

  // Clear filters
  const clearFilters = () => {
    setSearchQuery('');
    setSourceFilter('all');
    setOwnerFilter('all');
    setPriorityFilter('all');
    setStatusFilter('all');
    setDateFrom('');
    setDateTo('');
  };

  // Check for active filters
  const hasActiveFilters = sourceFilter !== 'all' || ownerFilter !== 'all' || 
    priorityFilter !== 'all' || statusFilter !== 'all' || lifecycleStageFilter !== 'all' || dateFrom || dateTo || searchQuery;

  // Handle contact selection
  const handleSelectContact = (contactId) => {
    setSelectedContacts(prev => 
      prev.includes(contactId) 
        ? prev.filter(id => id !== contactId)
        : [...prev, contactId]
    );
  };

  const handleSelectAll = () => {
    if (selectedContacts.length === contacts.length) {
      setSelectedContacts([]);
    } else {
      setSelectedContacts(contacts.map(c => c.id));
    }
  };

  // Quick Actions
  const handleAssignLead = async () => {
    if (!selectedAssignee) {
      toast.error('Please select a team member');
      return;
    }
    
    setIsProcessing(true);
    try {
      const contactIds = contactForAction ? [contactForAction.id] : selectedContacts;
      
      // Use PATCH endpoint for owner change
      await Promise.all(contactIds.map(id =>
        axios.patch(`${API}/contacts/${id}?token=${token}`, {
          owner_id: selectedAssignee
        })
      ));
      
      toast.success(`${contactIds.length} lead(s) assigned successfully`);
      setIsAssignDialogOpen(false);
      setSelectedAssignee('');
      setContactForAction(null);
      setSelectedContacts([]);
      fetchContacts();
      fetchCategoryCounts();
    } catch (error) {
      console.error('Error assigning lead:', error.response?.data || error);
      const errorMsg = error.response?.data?.detail || 'Failed to assign lead';
      toast.error(errorMsg);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleMarkAsJunk = async () => {
    if (!selectedJunkReason) {
      toast.error('Please select a reason');
      return;
    }
    
    setIsProcessing(true);
    try {
      const contactIds = contactForAction ? [contactForAction.id] : selectedContacts;
      
      await Promise.all(contactIds.map(id =>
        axios.put(`${API}/contacts/${id}?token=${token}`, {
          status: 'junk',
          junk_reason: selectedJunkReason,
          junk_notes: junkNotes,
          junk_marked_at: new Date().toISOString(),
          junk_marked_by: user?.id
        })
      ));
      
      toast.success(`${contactIds.length} lead(s) marked as junk`);
      setIsJunkDialogOpen(false);
      setSelectedJunkReason('');
      setJunkNotes('');
      setContactForAction(null);
      setSelectedContacts([]);
      fetchContacts();
      fetchCategoryCounts();
    } catch (error) {
      console.error('Error marking as junk:', error);
      toast.error('Failed to mark as junk');
    } finally {
      setIsProcessing(false);
    }
  };

  // True iff the contact has a follow-up scheduled in the future. Once the
  // due time has passed we treat the slot as empty so the user sees a fresh
  // "Schedule" form, not an "Update" form for a stale reminder.
  const hasActiveReminder = (() => {
    const raw = contactForAction?.next_followup_date;
    if (!raw) return false;
    const t = new Date(raw).getTime();
    return Number.isFinite(t) && t > Date.now();
  })();

  // Snooze info comes from FollowupReminderOverlay's localStorage map keyed
  // by `${contact_id}|${next_followup_date}`. Surface it here so the user
  // can see when the snoozed reminder will re-fire and the original time it
  // was scheduled for.
  const snoozeUntilMs = (() => {
    if (!contactForAction?.id || !contactForAction?.next_followup_date) return null;
    try {
      const raw = localStorage.getItem('followup_reminder_snoozed');
      if (!raw) return null;
      const map = JSON.parse(raw);
      const key = `${contactForAction.id}|${contactForAction.next_followup_date}`;
      const until = map[key];
      return typeof until === 'number' && until > Date.now() ? until : null;
    } catch {
      return null;
    }
  })();

  // When the dialog is opened against a single contact that has a *future*
  // scheduled follow-up, prefill the form so the user sees the existing
  // schedule and can update / clear it. Past schedules are treated as
  // empty — the previous reminder time has lapsed.
  useEffect(() => {
    if (!isFollowupDialogOpen) return;
    if (!hasActiveReminder) {
      setFollowupDate('');
      setFollowupNotes('');
      return;
    }
    try {
      const dt = new Date(contactForAction.next_followup_date);
      // Convert UTC ISO -> local "YYYY-MM-DDTHH:MM" for the datetime-local
      // input. Strip seconds; pad components.
      const pad = (n) => String(n).padStart(2, '0');
      setFollowupDate(
        `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`,
      );
    } catch {
      setFollowupDate('');
    }
    setFollowupNotes(contactForAction.followup_notes || '');
  }, [isFollowupDialogOpen, contactForAction, hasActiveReminder]);

  const handleClearFollowup = async () => {
    if (!contactForAction?.id) return;
    setIsProcessing(true);
    try {
      await axios.patch(`${API}/contacts/${contactForAction.id}?token=${token}`, {
        next_followup_date: null,
        followup_notes: '',
        actionable: false,
      });
      toast.success('Reminder cleared');
      setIsFollowupDialogOpen(false);
      setFollowupDate('');
      setFollowupNotes('');
      setContactForAction(null);
      fetchContacts();
      fetchCategoryCounts();
    } catch (error) {
      console.error('Error clearing follow-up:', error);
      toast.error('Failed to clear reminder');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleMarkAsFollowup = async () => {
    if (!followupDate) {
      toast.error('Please select a follow-up date');
      return;
    }
    
    setIsProcessing(true);
    try {
      const contactIds = contactForAction ? [contactForAction.id] : selectedContacts;
      
      // <input type="datetime-local"> returns a tz-naive string like
      // "2026-05-08T14:38" interpreted in the user's local timezone. The
      // backend stores everything in UTC and compares against UTC `now`, so
      // we have to convert to a real UTC ISO string here. new Date(...) on
      // the naive string parses it as local time, .toISOString() emits UTC.
      const followupIso = followupDate ? new Date(followupDate).toISOString() : followupDate;

      await Promise.all(contactIds.map(id =>
        axios.patch(`${API}/contacts/${id}?token=${token}`, {
          next_followup_date: followupIso,
          followup_notes: followupNotes,
          actionable: true
        })
      ));
      
      toast.success(`Follow-up scheduled for ${contactIds.length} lead(s)`);
      setIsFollowupDialogOpen(false);
      setFollowupDate('');
      setFollowupNotes('');
      setContactForAction(null);
      setSelectedContacts([]);
      fetchContacts();
      fetchCategoryCounts();
    } catch (error) {
      console.error('Error scheduling follow-up:', error);
      toast.error('Failed to schedule follow-up');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRestoreLead = async () => {
    setIsProcessing(true);
    try {
      const contactIds = contactForAction ? [contactForAction.id] : selectedContacts;
      
      await Promise.all(contactIds.map(id =>
        axios.put(`${API}/contacts/${id}?token=${token}`, {
          status: 'new',
          junk_reason: null,
          junk_notes: null,
          junk_marked_at: null,
          junk_marked_by: null
        })
      ));
      
      toast.success(`${contactIds.length} lead(s) restored`);
      setIsRestoreDialogOpen(false);
      setContactForAction(null);
      setSelectedContacts([]);
      fetchContacts();
      fetchCategoryCounts();
    } catch (error) {
      console.error('Error restoring lead:', error);
      toast.error('Failed to restore lead');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteLead = async () => {
    setIsProcessing(true);
    try {
      const contactIds = contactForAction ? [contactForAction.id] : selectedContacts;
      
      await Promise.all(contactIds.map(id =>
        axios.delete(`${API}/contacts/${id}?token=${token}`)
      ));
      
      toast.success(`${contactIds.length} lead(s) deleted permanently`);
      setIsDeleteDialogOpen(false);
      setContactForAction(null);
      setSelectedContacts([]);
      fetchContacts();
      fetchCategoryCounts();
    } catch (error) {
      console.error('Error deleting lead:', error);
      toast.error('Failed to delete lead');
    } finally {
      setIsProcessing(false);
    }
  };

  // Navigate to contact detail and mark as viewed
  const handleViewContact = async (contact) => {
    // Mark contact as viewed in the background (for new leads tracking)
    try {
      await axios.post(`${API}/contacts/${contact.id}/mark-viewed?token=${token}`);
      // Refresh counts after marking as viewed
      fetchCategoryCounts();
    } catch (error) {
      // Don't block navigation if marking fails
      console.error('Error marking contact as viewed:', error);
    }
    navigate(`/contacts/${contact.id}`);
  };

  // Get initials for avatar
  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Check if follow-up is overdue
  const isOverdue = (date) => {
    if (!date) return false;
    return new Date(date) < new Date();
  };

  // Check if user is an agent (sales_rep, sales_agent, telesales, agent)
  const isAgentRole = user?.role && ['sales_rep', 'sales_agent', 'telesales', 'agent'].includes(
    user.role.toLowerCase().replace(' ', '_').replace('-', '_')
  );

  // Render category tabs
  const renderCategoryTabs = () => {
    // Filter categories based on user role - hide "Assigned" tab for agents
    const visibleCategories = Object.values(LEAD_CATEGORIES).filter(category => {
      if (category.id === 'assigned' && isAgentRole) {
        return false; // Hide Assigned tab for agents
      }
      return true;
    });
    
    return (
      <div className="flex flex-wrap gap-3 mb-6 min-h-[72px] items-start">
        {visibleCategories.map(category => {
          const Icon = category.icon;
          const isActive = activeCategory === category.id;
          const count = categoryCounts[category.id] || 0;
          
          // Get theme-aware styles
          const bgColor = isDarkMode ? category.bgColor.dark : category.bgColor.light;
          const textColor = isDarkMode ? category.textColor.dark : category.textColor.light;
          const borderColor = isDarkMode ? category.borderColor.dark : category.borderColor.light;
          const iconBg = isDarkMode ? category.iconBg.dark : category.iconBg.light;
          
          // Special styling for "All Leads" tab - dark background with white text when active
          const activeStyles = isActive 
            ? `${bgColor} ${borderColor} ${textColor} shadow-lg`
            : isDarkMode 
              ? 'bg-card border-border text-muted-foreground hover:border-muted-foreground/50 hover:bg-muted/50'
              : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50';
          
          const iconBgStyles = isActive ? iconBg : isDarkMode ? 'bg-muted' : 'bg-slate-100';
          const iconTextStyles = isActive ? textColor : isDarkMode ? 'text-muted-foreground' : 'text-slate-500';
          
          // Handle category change - clear assigned filters when leaving "assigned" tab
          const handleCategoryChange = (categoryId) => {
            if (categoryId !== 'assigned') {
              // Clear assigned date filters when switching away from Assigned tab
              setAssignedFrom('');
              setAssignedTo('');
            }
            setActiveCategory(categoryId);
          };
          
          return (
            <button
              key={category.id}
              onClick={() => handleCategoryChange(category.id)}
              data-testid={`tab-${category.id}`}
              className={`
                flex items-center gap-3 px-5 py-3 rounded-xl border-2 transition-colors duration-200
                h-[68px] min-w-[160px] box-border
                ${activeStyles}
              `}
              style={{ 
                boxSizing: 'border-box',
                transform: 'none'
              }}
            >
              <div className={`p-2 rounded-lg flex-shrink-0 ${iconBgStyles}`}>
                <Icon size={18} className={iconTextStyles} />
              </div>
              <div className="text-left flex-1 min-w-0">
                <div className="font-semibold text-sm leading-5 whitespace-nowrap">{category.label}</div>
                <div className={`text-xs leading-4 ${isActive ? 'opacity-80' : isDarkMode ? 'text-muted-foreground' : 'text-slate-400'}`}>
                  {count} {count === 1 ? 'lead' : 'leads'}
                </div>
              </div>
              {count > 0 && category.id === 'followup' && (
                <span className="ml-2 w-2 h-2 rounded-full bg-amber-500 animate-pulse flex-shrink-0" />
              )}
            </button>
          );
        })}
      </div>
    );
  };

  // Render bulk actions based on category
  const renderBulkActions = () => {
    if (selectedContacts.length === 0) return null;

    return (
      <div className="flex items-center gap-2 p-3 bg-slate-900 text-white rounded-lg mb-4">
        <span className="text-sm font-medium">{selectedContacts.length} selected</span>
        <div className="flex-1" />
        
        {activeCategory === 'all' && (
          <>
            {canAssignLeads && (
              <Button size="sm" variant="secondary" onClick={() => setIsAssignDialogOpen(true)} data-testid="bulk-assign-btn">
                <UserPlus size={14} className="mr-1" /> Assign
              </Button>
            )}
            <Button size="sm" variant="secondary" onClick={() => setIsFollowupDialogOpen(true)} data-testid="bulk-followup-btn">
              <Bell size={14} className="mr-1" /> Follow-up
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setIsJunkDialogOpen(true)} data-testid="bulk-junk-btn">
              <Ban size={14} className="mr-1" /> Mark Junk
            </Button>
          </>
        )}
        
        {activeCategory === 'new' && (
          <>
            {canAssignLeads && (
              <Button size="sm" variant="secondary" onClick={() => setIsAssignDialogOpen(true)} data-testid="bulk-assign-btn">
                <UserPlus size={14} className="mr-1" /> Assign
              </Button>
            )}
            <Button size="sm" variant="secondary" onClick={() => setIsFollowupDialogOpen(true)} data-testid="bulk-followup-btn">
              <Bell size={14} className="mr-1" /> Follow-up
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setIsJunkDialogOpen(true)} data-testid="bulk-junk-btn">
              <Ban size={14} className="mr-1" /> Mark Junk
            </Button>
          </>
        )}
        
        {activeCategory === 'followup' && (
          <>
            <Button size="sm" variant="secondary" onClick={() => setIsAssignDialogOpen(true)}>
              <UserPlus size={14} className="mr-1" /> Assign
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setIsJunkDialogOpen(true)}>
              <Ban size={14} className="mr-1" /> Mark Junk
            </Button>
          </>
        )}
        
        {activeCategory === 'junk' && (
          <>
            <Button size="sm" variant="secondary" onClick={() => setIsRestoreDialogOpen(true)} data-testid="bulk-restore-btn">
              <RotateCcw size={14} className="mr-1" /> Restore
            </Button>
            <Button size="sm" variant="destructive" onClick={() => setIsDeleteDialogOpen(true)} data-testid="bulk-delete-btn">
              <Trash2 size={14} className="mr-1" /> Delete
            </Button>
          </>
        )}
        
        {activeCategory === 'assigned' && (
          <>
            <Button size="sm" variant="secondary" onClick={() => setIsAssignDialogOpen(true)}>
              <UserPlus size={14} className="mr-1" /> Reassign
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setIsFollowupDialogOpen(true)}>
              <Bell size={14} className="mr-1" /> Follow-up
            </Button>
          </>
        )}
        
        <Button size="sm" variant="ghost" onClick={() => setSelectedContacts([])} className="text-white hover:bg-slate-800">
          <X size={14} />
        </Button>
      </div>
    );
  };

  // Render contact row based on category
  const renderContactRow = (contact) => {
    // Backend exposes the assigned user via several different shapes
    // depending on which legacy path stamped the row: a nested
    // `owner` / `assignee` object, or just flat `owner_name` /
    // `assigned_to_name` strings. Normalise them so the column can
    // display whatever's present — otherwise the row appears
    // "Unassigned" even though owner_id is set, which is the bug
    // that made the Assigned tab look broken.
    const ownerName =
      contact.owner?.full_name ||
      contact.owner?.email ||
      contact.assignee?.full_name ||
      contact.assignee?.email ||
      contact.assigned_to_name ||
      contact.owner_name ||
      null;
    const ownerEmail =
      contact.owner?.email || contact.assignee?.email || null;
    const owner = ownerName
      ? { full_name: ownerName, email: ownerEmail }
      : null;
    const status = contact.status || 'new';
    const statusStyles = getStatusStyles(isDarkMode);
    const priorityStyles = getPriorityStyles(isDarkMode);
    const statusStyle = statusStyles[status] || statusStyles.new;
    const priority = contact.priority || 'normal';
    const priorityStyle = priorityStyles[priority] || priorityStyles.normal;
    const overdueFollowup = contact.next_followup_date && isOverdue(contact.next_followup_date);

    return (
      <TableRow 
        key={contact.id} 
        className={`cursor-pointer group ${isDarkMode ? 'hover:bg-muted/50' : 'hover:bg-slate-50/50'}`}
        onClick={() => handleViewContact(contact)}
        data-testid={`contact-row-${contact.id}`}
      >
        {/* Checkbox */}
        <TableCell className="w-12" onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={selectedContacts.includes(contact.id)}
            onCheckedChange={() => handleSelectContact(contact.id)}
            data-testid={`checkbox-${contact.id}`}
          />
        </TableCell>

        {/* Name & Contact Info */}
        <TableCell>
          <div className="flex items-center gap-3">
            <Avatar className={`h-9 w-9 ${isDarkMode ? 'bg-gradient-to-br from-slate-700 to-slate-800' : 'bg-gradient-to-br from-slate-100 to-slate-200'}`}>
              <AvatarFallback className={`text-xs font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                {getInitials(contact.name)}
              </AvatarFallback>
            </Avatar>
            <div>
              <div className={`font-medium group-hover:text-blue-500 transition-colors ${isDarkMode ? 'text-foreground' : 'text-slate-900'}`}>
                {contact.name || 'Unknown'}
              </div>
              <div className={`text-xs flex items-center gap-2 ${isDarkMode ? 'text-muted-foreground' : 'text-slate-500'}`}>
                {contact.email && <span>{contact.email}</span>}
                {contact.phone && <span>• {contact.phone}</span>}
              </div>
            </div>
          </div>
        </TableCell>

        {/* Source */}
        <TableCell>
          <span className={`text-sm ${isDarkMode ? 'text-muted-foreground' : 'text-slate-600'}`}>{contact.source || '-'}</span>
        </TableCell>

        {/* Campaign/UTM */}
        <TableCell>
          <div className="space-y-0.5">
            {contact.utm_campaign ? (
              <span className={`text-xs px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-purple-900/30 text-purple-300' : 'bg-purple-50 text-purple-700'}`}>
                {contact.utm_campaign}
              </span>
            ) : contact.utm_source ? (
              <span className={`text-xs px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-50 text-blue-700'}`}>
                {contact.utm_source}
              </span>
            ) : (
              <span className={`text-xs ${isDarkMode ? 'text-muted-foreground' : 'text-slate-400'}`}>-</span>
            )}
            {contact.utm_medium && (
              <div className={`text-[10px] ${isDarkMode ? 'text-muted-foreground' : 'text-slate-400'}`}>
                via {contact.utm_medium}
              </div>
            )}
          </div>
        </TableCell>

        {/* Category-specific columns */}
        {activeCategory === 'all' && (
          <>
            <TableCell>
              <Badge className={`${statusStyle.bg} ${statusStyle.text} ${statusStyle.border} border text-xs`}>
                {status.replace(/_/g, ' ')}
              </Badge>
            </TableCell>
            <TableCell>
              <div className="flex items-center gap-2">
                {owner ? (
                  <>
                    <Avatar className="h-6 w-6">
                      <AvatarFallback className={`text-[10px] ${isDarkMode ? 'bg-emerald-800/50 text-emerald-300' : 'bg-emerald-100 text-emerald-700'}`}>
                        {getInitials(owner?.full_name || owner?.email)}
                      </AvatarFallback>
                    </Avatar>
                    <span className={`text-sm ${isDarkMode ? 'text-foreground' : 'text-slate-700'}`}>{owner?.full_name || owner?.email}</span>
                  </>
                ) : (
                  <span className={`text-sm italic ${isDarkMode ? 'text-muted-foreground' : 'text-slate-400'}`}>Unassigned</span>
                )}
              </div>
            </TableCell>
            <TableCell>
              <span
                className={`text-sm ${isDarkMode ? 'text-muted-foreground' : 'text-slate-500'}`}
                title={contact.created_at ? formatDateTime(contact.created_at) : ''}
              >
                {contact.created_at ? formatRelativeTime(contact.created_at) : '-'}
              </span>
            </TableCell>
          </>
        )}

        {activeCategory === 'new' && (
          <>
            <TableCell>
              <span
                className={`text-sm ${isDarkMode ? 'text-muted-foreground' : 'text-slate-500'}`}
                title={contact.created_at ? formatDateTime(contact.created_at) : ''}
              >
                {contact.created_at ? formatRelativeTime(contact.created_at) : '-'}
              </span>
            </TableCell>
            <TableCell>
              <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${priorityStyle.bg} ${priorityStyle.text}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${priorityStyle.dot}`} />
                {priority.charAt(0).toUpperCase() + priority.slice(1)}
              </div>
            </TableCell>
          </>
        )}

        {activeCategory === 'followup' && (
          <>
            <TableCell>
              <div
                className={`flex items-center gap-1.5 text-sm ${overdueFollowup ? 'text-red-500 font-medium' : isDarkMode ? 'text-muted-foreground' : 'text-slate-600'}`}
                title={contact.next_followup_date ? formatDateTime(contact.next_followup_date) : ''}
              >
                {overdueFollowup && <AlertCircle size={14} className="text-red-500" />}
                {contact.next_followup_date ? formatDateTime(contact.next_followup_date) : '-'}
              </div>
            </TableCell>
            <TableCell>
              <span className={`text-sm ${isDarkMode ? 'text-muted-foreground' : 'text-slate-500'}`}>
                {contact.last_activity_at ? formatRelativeTime(contact.last_activity_at) : '-'}
              </span>
            </TableCell>
            <TableCell>
              <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${priorityStyle.bg} ${priorityStyle.text}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${priorityStyle.dot}`} />
                {priority.charAt(0).toUpperCase() + priority.slice(1)}
              </div>
            </TableCell>
          </>
        )}

        {activeCategory === 'junk' && (
          <>
            <TableCell>
              <Badge variant="outline" className="text-xs">
                {contact.junk_reason ? JUNK_REASONS.find(r => r.id === contact.junk_reason)?.label || contact.junk_reason : 'No reason'}
              </Badge>
            </TableCell>
            <TableCell>
              <span className={`text-sm ${isDarkMode ? 'text-muted-foreground' : 'text-slate-500'}`}>
                {contact.junk_marked_at ? formatRelativeTime(contact.junk_marked_at) : '-'}
              </span>
            </TableCell>
          </>
        )}

        {activeCategory === 'assigned' && (
          <>
            <TableCell>
              <div className="flex items-center gap-2">
                <Avatar className="h-6 w-6">
                  <AvatarFallback className={`text-[10px] ${isDarkMode ? 'bg-emerald-800/50 text-emerald-300' : 'bg-emerald-100 text-emerald-700'}`}>
                    {getInitials(owner?.full_name || owner?.email)}
                  </AvatarFallback>
                </Avatar>
                <span className={`text-sm ${isDarkMode ? 'text-foreground' : 'text-slate-700'}`}>{owner?.full_name || owner?.email || 'Unassigned'}</span>
              </div>
            </TableCell>
            <TableCell>
              <Badge className={`${statusStyle.bg} ${statusStyle.text} ${statusStyle.border} border text-xs`}>
                {status.replace(/_/g, ' ')}
              </Badge>
            </TableCell>
            <TableCell>
              <span className={`text-sm ${isDarkMode ? 'text-muted-foreground' : 'text-slate-500'}`}>
                {contact.last_activity_at ? formatRelativeTime(contact.last_activity_at) : '-'}
              </span>
            </TableCell>
          </>
        )}

        {/* Quick Actions — always visible (was hover-only, which
            hid the affordance from anyone scanning the list with a
            keyboard or on touch). */}
        <TableCell onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center gap-1">
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-8 w-8 p-0"
              onClick={() => window.location.href = `tel:${contact.phone}`}
              disabled={!contact.phone}
              data-testid={`call-${contact.id}`}
            >
              <Phone size={14} className={isDarkMode ? 'text-muted-foreground' : 'text-slate-500'} />
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-8 w-8 p-0"
              onClick={() => {
                setEmailContact(contact);
                setIsEmailComposerOpen(true);
              }}
              disabled={!contact.email}
              data-testid={`email-${contact.id}`}
            >
              <Mail size={14} className={isDarkMode ? 'text-muted-foreground' : 'text-slate-500'} />
            </Button>
            
            {activeCategory === 'all' && (
              <>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-8 w-8 p-0"
                  onClick={() => {
                    setContactForAction(contact);
                    setIsAssignDialogOpen(true);
                  }}
                  data-testid={`assign-${contact.id}`}
                >
                  <UserPlus size={14} className={isDarkMode ? 'text-muted-foreground' : 'text-slate-500'} />
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-8 w-8 p-0"
                  onClick={() => {
                    setContactForAction(contact);
                    setIsFollowupDialogOpen(true);
                  }}
                  data-testid={`followup-${contact.id}`}
                >
                  <Bell size={14} className={isDarkMode ? 'text-muted-foreground' : 'text-slate-500'} />
                </Button>
              </>
            )}
            
            {activeCategory === 'new' && (
              <>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-8 w-8 p-0"
                  onClick={() => {
                    setContactForAction(contact);
                    setIsAssignDialogOpen(true);
                  }}
                  data-testid={`assign-${contact.id}`}
                >
                  <UserPlus size={14} className={isDarkMode ? 'text-muted-foreground' : 'text-slate-500'} />
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-8 w-8 p-0"
                  onClick={() => {
                    setContactForAction(contact);
                    setIsJunkDialogOpen(true);
                  }}
                  data-testid={`junk-${contact.id}`}
                >
                  <Ban size={14} className={isDarkMode ? 'text-muted-foreground' : 'text-slate-500'} />
                </Button>
              </>
            )}
            
            {activeCategory === 'junk' && (
              <>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-8 w-8 p-0"
                  onClick={() => {
                    setContactForAction(contact);
                    setIsRestoreDialogOpen(true);
                  }}
                  data-testid={`restore-${contact.id}`}
                >
                  <RotateCcw size={14} className="text-emerald-500" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                  onClick={() => {
                    setContactForAction(contact);
                    setIsDeleteDialogOpen(true);
                  }}
                  data-testid={`delete-${contact.id}`}
                >
                  <Trash2 size={14} />
                </Button>
              </>
            )}
          </div>
        </TableCell>
      </TableRow>
    );
  };

  // Get table headers based on category
  const getTableHeaders = () => {
    const baseHeaders = [
      { key: 'select', label: '', width: 'w-12' },
      { key: 'name', label: 'Name', sortable: true },
      { key: 'source', label: 'Source', sortable: true },
      { key: 'campaign', label: 'Campaign/UTM', sortable: false }
    ];

    switch (activeCategory) {
      case 'all':
        return [
          ...baseHeaders,
          { key: 'status', label: 'Status', sortable: true },
          { key: 'owner', label: 'Assigned To' },
          { key: 'created_at', label: 'Created', sortable: true },
          { key: 'actions', label: '' }
        ];
      case 'new':
        return [
          ...baseHeaders,
          { key: 'created_at', label: 'Created', sortable: true },
          { key: 'priority', label: 'Priority', sortable: true },
          { key: 'actions', label: '' }
        ];
      case 'followup':
        return [
          ...baseHeaders,
          { key: 'next_followup_date', label: 'Follow-up Date', sortable: true },
          { key: 'last_activity_at', label: 'Last Activity', sortable: true },
          { key: 'priority', label: 'Priority', sortable: true },
          { key: 'actions', label: '' }
        ];
      case 'junk':
        return [
          ...baseHeaders,
          { key: 'junk_reason', label: 'Reason' },
          { key: 'junk_marked_at', label: 'Marked On', sortable: true },
          { key: 'actions', label: '' }
        ];
      case 'assigned':
        return [
          ...baseHeaders,
          { key: 'owner', label: 'Assigned To' },
          { key: 'status', label: 'Status', sortable: true },
          { key: 'last_activity_at', label: 'Last Activity', sortable: true },
          { key: 'actions', label: '' }
        ];
      default:
        return baseHeaders;
    }
  };

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-background' : 'bg-gradient-to-br from-slate-50 via-white to-slate-50'}`}>
      <div className="max-w-[1600px] mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8">
          <div>
            <h1 className={`text-2xl font-bold flex items-center gap-2 flex-wrap ${isDarkMode ? 'text-foreground' : 'text-slate-900'}`}>
              Lead Dashboard
              <HelpInfo
                align="inline"
                title="How the Lead Dashboard works"
                steps={[
                  <>This is the central index of every lead in the CRM — anyone who's made contact through any channel (web form, WhatsApp, walk-in, broker referral, paid ad). One row per lead with status, owner, source, and the most recent activity at a glance.</>,
                  <><strong>Top tiles</strong> — quick filters by lifecycle bucket: <em>All Leads</em>, <em>New Leads</em> (just landed, no first touch yet), <em>Follow-up</em> (have a scheduled next contact), <em>Junk</em> (marked unqualified — duplicates, spam, wrong number), <em>Assigned</em> (have an owner), and <em>UTM Tracker</em> (filtered to ad-source leads with attribution).</>,
                  <><strong>Search + filters</strong> — text search by name / email / phone runs across every field at once. The three dropdowns layer on top: <em>All Sources</em> (Website, WhatsApp, broker, Meta, Google…), <em>All Owners</em> (the agent who owns the lead today), <em>All Statuses</em> (New, Contacted, Qualified, Site Visit, Negotiation, Booked, Closed, Lost). <em>More Filters</em> opens advanced filters — date ranges, lead score, campaign, project, temperature.</>,
                  <><strong>Each row shows:</strong> name + email + phone, <em>Source</em> (channel the lead came from), <em>Campaign / UTM</em> (when paid traffic — e.g. <code>dubai-luxury-2024 · via cpc</code>), <em>Status</em> badge, <em>Assigned To</em> (current owner), and <em>Created</em> (relative time). Click any row to open the full Contact Detail with timeline, activities and quick actions.</>,
                  <><strong>Add Lead</strong> opens a quick-create dialog (name, phone, email, source). Auto-assigned via your team's routing rules. For bulk uploads use <em>More Filters &gt; Import CSV</em>.</>,
                  <><strong>Export</strong> sends the current filter set to CSV — handy for offline review or sharing with HOD / leadership. The export honours every filter you have applied.</>,
                  <><strong>Sort &amp; pagination</strong> — sort by date created, last contacted, lead score, or status. Showing 25 at a time; scroll or use the page controls at the bottom to see more.</>,
                  <><strong>Refresh</strong> pulls the latest data without losing your filters. The <em>Data as of</em> timestamp under the title tells you when the page was last fetched.</>,
                ]}
                tip="Click the All / New / Follow-up tiles to switch views — they're not just summary numbers, they're working filters."
              />
            </h1>
            <p className={`mt-1 ${isDarkMode ? 'text-muted-foreground' : 'text-slate-500'}`}>Manage and track your leads efficiently</p>
            {contactsFetchedAt && (
              <p className={`text-xs mt-1 ${isDarkMode ? 'text-muted-foreground/80' : 'text-slate-400'}`}>
                Data as of {contactsFetchedAt.toLocaleString(undefined, {
                  day: '2-digit', month: 'short', year: 'numeric',
                  hour: '2-digit', minute: '2-digit',
                })}
              </p>
            )}
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportToExcel}
              disabled={isExporting || totalContacts === 0}
              className="gap-2"
              data-testid="export-leads-btn"
            >
              {isExporting ? (
                <RefreshCw size={14} className="animate-spin" />
              ) : (
                <Download size={14} />
              )}
              Export
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { fetchContacts(); fetchCategoryCounts(); }}
              className="gap-2"
              data-testid="refresh-btn"
            >
              <RefreshCw size={14} />
              Refresh
            </Button>
            <PermissionGate permission="SALES.CONTACTS" action="create">
              <Button 
                onClick={() => setIsAddLeadDialogOpen(true)}
                className="gap-2 bg-slate-900 hover:bg-slate-800"
                data-testid="add-lead-btn"
              >
                <Plus size={16} />
                Add Lead
              </Button>
            </PermissionGate>
          </div>
        </div>

        {/* Category Tabs */}
        {renderCategoryTabs()}

        {/* Search and Filters */}
        <Card className={`p-4 mb-6 ${isDarkMode ? 'border-border bg-card' : 'border-slate-200'}`}>
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-muted-foreground' : 'text-slate-400'}`} />
              <Input
                placeholder="Search by name, email, or phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`pl-9 h-10 ${isDarkMode ? 'bg-background border-border' : 'bg-white border-slate-200'}`}
                data-testid="search-input"
              />
            </div>

            {/* Quick Filters */}
            <div className="flex flex-wrap items-center gap-2">
              <Select value={sourceFilter} onValueChange={setSourceFilter}>
                <SelectTrigger className={`w-[140px] h-9 ${isDarkMode ? 'bg-background' : 'bg-white'}`} data-testid="source-filter">
                  <SelectValue placeholder="All Sources" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  {filterOptions.sources?.map(source => (
                    <SelectItem key={source} value={source}>{source}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {(activeCategory === 'assigned' || activeCategory === 'all') && (
                <Select value={ownerFilter} onValueChange={setOwnerFilter}>
                  <SelectTrigger className={`w-[140px] h-9 ${isDarkMode ? 'bg-background' : 'bg-white'}`} data-testid="owner-filter">
                    <SelectValue placeholder="All Owners" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Owners</SelectItem>
                    {allUsers.map(user => (
                      <SelectItem key={user.id} value={user.id}>{user.full_name || user.email}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {activeCategory === 'all' && (
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className={`w-[140px] h-9 ${isDarkMode ? 'bg-background' : 'bg-white'}`} data-testid="status-filter">
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="lead">Lead</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="qualified">Qualified</SelectItem>
                    <SelectItem value="opportunity">Opportunity</SelectItem>
                    <SelectItem value="customer">Customer</SelectItem>
                    <SelectItem value="junk">Junk</SelectItem>
                  </SelectContent>
                </Select>
              )}

              <Button
                variant={showFilters ? "default" : "outline"}
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                className="h-9 gap-1.5"
                data-testid="toggle-filters-btn"
              >
                <Filter size={14} />
                More Filters
              </Button>

              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className={`h-9 ${isDarkMode ? 'text-muted-foreground' : 'text-slate-500'}`}>
                  <X size={14} className="mr-1" />
                  Clear
                </Button>
              )}
            </div>
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <div className={`mt-4 pt-4 border-t grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 ${isDarkMode ? 'border-border' : 'border-slate-200'}`}>
              <div>
                <Label className={`text-xs mb-1.5 block ${isDarkMode ? 'text-muted-foreground' : 'text-slate-500'}`}>Lifecycle Stage</Label>
                <Select value={lifecycleStageFilter} onValueChange={setLifecycleStageFilter}>
                  <SelectTrigger className={`h-9 ${isDarkMode ? 'bg-background' : 'bg-white'}`}>
                    <SelectValue placeholder="All Stages" />
                  </SelectTrigger>
                  <SelectContent>
                    {lifecycleStages.map(stage => (
                      <SelectItem key={stage.value} value={stage.value}>{stage.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className={`text-xs mb-1.5 block ${isDarkMode ? 'text-muted-foreground' : 'text-slate-500'}`}>Priority</Label>
                <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                  <SelectTrigger className={`h-9 ${isDarkMode ? 'bg-background' : 'bg-white'}`}>
                    <SelectValue placeholder="All Priorities" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Priorities</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className={`text-xs mb-1.5 block ${isDarkMode ? 'text-muted-foreground' : 'text-slate-500'}`}>Quick Date Filter</Label>
                <Select value={quickDateFilter} onValueChange={handleQuickDateChange}>
                  <SelectTrigger className={`h-9 ${isDarkMode ? 'bg-background' : 'bg-white'}`} data-testid="quick-date-filter">
                    <SelectValue placeholder="Select date range" />
                  </SelectTrigger>
                  <SelectContent>
                    {quickDateOptions.map(option => (
                      <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className={`text-xs mb-1.5 block ${isDarkMode ? 'text-muted-foreground' : 'text-slate-500'}`}>Lead Created From</Label>
                <Input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setQuickDateFilter('custom'); }} className="h-9" data-testid="date-from-filter" />
              </div>
              <div>
                <Label className={`text-xs mb-1.5 block ${isDarkMode ? 'text-muted-foreground' : 'text-slate-500'}`}>Lead Created To</Label>
                <Input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setQuickDateFilter('custom'); }} className="h-9" data-testid="date-to-filter" />
              </div>
              {/* Lead Assigned filters - only show on "Assigned" tab */}
              {activeCategory === 'assigned' && (
                <>
                  <div>
                    <Label className={`text-xs mb-1.5 block ${isDarkMode ? 'text-muted-foreground' : 'text-slate-500'}`}>Lead Assigned From</Label>
                    <Input type="date" value={assignedFrom} onChange={(e) => setAssignedFrom(e.target.value)} className="h-9" data-testid="assigned-from-filter" />
                  </div>
                  <div>
                    <Label className={`text-xs mb-1.5 block ${isDarkMode ? 'text-muted-foreground' : 'text-slate-500'}`}>Lead Assigned To</Label>
                    <Input type="date" value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)} className="h-9" data-testid="assigned-to-filter" />
                  </div>
                </>
              )}
            </div>
          )}
        </Card>

        {/* Bulk Actions */}
        {renderBulkActions()}

        {/* Results Summary */}
        <div className="flex items-center justify-between mb-4">
          <p className={`text-sm ${isDarkMode ? 'text-muted-foreground' : 'text-slate-500'}`}>
            Showing {contacts.length} of {totalContacts} leads
          </p>
          <div className="flex items-center gap-2">
            <span className={`text-sm ${isDarkMode ? 'text-muted-foreground' : 'text-slate-500'}`}>Sort by:</span>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[140px] h-8 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="created_at">Date Created</SelectItem>
                <SelectItem value="assigned_at">Date Assigned</SelectItem>
                <SelectItem value="updated_at">Last Updated</SelectItem>
                <SelectItem value="name">Name</SelectItem>
                <SelectItem value="last_activity_at">Last Activity</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
              className="h-8 w-8 p-0"
            >
              {sortOrder === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
            </Button>
          </div>
        </div>

        {/* Contacts Table */}
        <Card className={`overflow-hidden ${isDarkMode ? 'border-border' : 'border-slate-200'}`}>
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <RefreshCw className={`h-8 w-8 animate-spin ${isDarkMode ? 'text-muted-foreground' : 'text-slate-400'}`} />
            </div>
          ) : contacts.length === 0 ? (
            <div className={`flex flex-col items-center justify-center h-64 ${isDarkMode ? 'text-muted-foreground' : 'text-slate-500'}`}>
              <Inbox size={48} className={`mb-4 ${isDarkMode ? 'text-muted-foreground/30' : 'text-slate-300'}`} />
              <p className={`text-lg font-medium ${isDarkMode ? 'text-foreground' : ''}`}>No leads found</p>
              <p className="text-sm">Try adjusting your filters or add new leads</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className={isDarkMode ? 'bg-muted/50 hover:bg-muted/50' : 'bg-slate-50/50 hover:bg-slate-50/50'}>
                  {getTableHeaders().map(header => (
                    <TableHead 
                      key={header.key} 
                      className={`${header.width || ''} ${header.sortable ? 'cursor-pointer hover:bg-muted' : ''}`}
                      onClick={() => {
                        if (header.sortable) {
                          if (sortBy === header.key) {
                            setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
                          } else {
                            setSortBy(header.key);
                            setSortOrder('desc');
                          }
                        }
                      }}
                    >
                      {header.key === 'select' ? (
                        <Checkbox
                          checked={selectedContacts.length === contacts.length && contacts.length > 0}
                          onCheckedChange={handleSelectAll}
                          data-testid="select-all-checkbox"
                        />
                      ) : (
                        <div className="flex items-center gap-1">
                          {header.label}
                          {header.sortable && sortBy === header.key && (
                            sortOrder === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />
                          )}
                        </div>
                      )}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {contacts.map(contact => renderContactRow(contact))}
              </TableBody>
            </Table>
          )}
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <p className={`text-sm ${isDarkMode ? 'text-muted-foreground' : 'text-slate-500'}`}>
              Page {currentPage} of {totalPages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}

        {/* Assign Dialog */}
        <Dialog open={isAssignDialogOpen} onOpenChange={setIsAssignDialogOpen}>
          <DialogContent className="sm:max-w-md bg-white p-6">
            <DialogHeader className="pb-4">
              <DialogTitle>Assign Lead</DialogTitle>
              <DialogDescription>
                Select a team member to assign {contactForAction ? 'this lead' : `${selectedContacts.length} lead(s)`} to.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Label className={`text-sm block ${isDarkMode ? 'text-muted-foreground' : 'text-slate-600'}`}>Team Member</Label>
              <Select value={selectedAssignee} onValueChange={setSelectedAssignee}>
                <SelectTrigger data-testid="assignee-select">
                  <SelectValue placeholder="Select team member" />
                </SelectTrigger>
                <SelectContent>
                  {allUsers.map(user => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.full_name || user.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsAssignDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleAssignLead} disabled={isProcessing} data-testid="confirm-assign-btn">
                {isProcessing ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
                Assign
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Mark as Junk Dialog */}
        <Dialog open={isJunkDialogOpen} onOpenChange={setIsJunkDialogOpen}>
          <DialogContent className="sm:max-w-md bg-white p-6">
            <DialogHeader className="pb-4">
              <DialogTitle>Mark as Junk</DialogTitle>
              <DialogDescription>
                Please select a reason for marking {contactForAction ? 'this lead' : `${selectedContacts.length} lead(s)`} as junk.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-5">
              <div>
                <Label className={`text-sm mb-2 block ${isDarkMode ? 'text-muted-foreground' : 'text-slate-600'}`}>Reason</Label>
                <Select value={selectedJunkReason} onValueChange={setSelectedJunkReason}>
                  <SelectTrigger data-testid="junk-reason-select">
                    <SelectValue placeholder="Select reason" />
                  </SelectTrigger>
                  <SelectContent>
                    {JUNK_REASONS.map(reason => (
                      <SelectItem key={reason.id} value={reason.id}>
                        {reason.icon} {reason.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className={`text-sm mb-2 block ${isDarkMode ? 'text-muted-foreground' : 'text-slate-600'}`}>Notes (optional)</Label>
                <Textarea
                  value={junkNotes}
                  onChange={(e) => setJunkNotes(e.target.value)}
                  placeholder="Add any additional notes..."
                  className="resize-none"
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsJunkDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleMarkAsJunk} disabled={isProcessing} variant="destructive" data-testid="confirm-junk-btn">
                {isProcessing ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
                Mark as Junk
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Follow-up Dialog */}
        <Dialog open={isFollowupDialogOpen} onOpenChange={setIsFollowupDialogOpen}>
          <DialogContent className="sm:max-w-md bg-white p-6">
            <DialogHeader className="pb-4">
              <DialogTitle>Schedule Follow-up</DialogTitle>
              <DialogDescription>
                Set a follow-up date for {contactForAction ? 'this lead' : `${selectedContacts.length} lead(s)`}.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-5">
              {/* Existing-reminder banner: only shows when editing a single
                  contact whose scheduled time is still in the future. Lists
                  the original schedule, snooze count, and last dismissed
                  time as audit context. */}
              {hasActiveReminder && (
                <div className="flex items-start gap-3 p-3 rounded-lg border border-purple-200 bg-purple-50 text-sm">
                  <Bell size={16} className="text-purple-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 space-y-1">
                    <p className="font-medium text-purple-900">Reminder already scheduled</p>
                    <p className="text-purple-700">
                      <strong>Current:</strong> {new Date(contactForAction.next_followup_date).toLocaleString()}
                    </p>
                    {contactForAction.original_followup_date
                      && contactForAction.original_followup_date !== contactForAction.next_followup_date && (
                      <p className="text-purple-700">
                        <strong>Originally:</strong> {new Date(contactForAction.original_followup_date).toLocaleString()}
                      </p>
                    )}
                    {contactForAction.snooze_count > 0 && (
                      <p className="text-amber-700">
                        <strong>Snoozed:</strong> {contactForAction.snooze_count} time{contactForAction.snooze_count === 1 ? '' : 's'}
                        {contactForAction.snoozed_at && ` · last at ${new Date(contactForAction.snoozed_at).toLocaleString()}`}
                      </p>
                    )}
                    {contactForAction.last_dismissed_at && (
                      <p className="text-purple-700">
                        <strong>Last dismissed:</strong> {new Date(contactForAction.last_dismissed_at).toLocaleString()}
                      </p>
                    )}
                    <p className="text-purple-700/80 text-xs pt-1">Update the date below or clear it.</p>
                  </div>
                </div>
              )}
              <div>
                <Label className={`text-sm mb-2 block ${isDarkMode ? 'text-muted-foreground' : 'text-slate-600'}`}>Follow-up Date</Label>
                <Input
                  type="datetime-local"
                  value={followupDate}
                  onChange={(e) => setFollowupDate(e.target.value)}
                  data-testid="followup-date-input"
                />
              </div>
              <div>
                <Label className={`text-sm mb-2 block ${isDarkMode ? 'text-muted-foreground' : 'text-slate-600'}`}>Notes (optional)</Label>
                <Textarea
                  value={followupNotes}
                  onChange={(e) => setFollowupNotes(e.target.value)}
                  placeholder="Add follow-up notes..."
                  className="resize-none"
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter className="flex justify-between sm:justify-between">
              {/* Clear-reminder button only appears when a future-dated
                  reminder is already set */}
              {hasActiveReminder ? (
                <Button
                  variant="ghost"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={handleClearFollowup}
                  disabled={isProcessing}
                  data-testid="clear-followup-btn"
                >
                  Clear reminder
                </Button>
              ) : <span />}
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setIsFollowupDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleMarkAsFollowup} disabled={isProcessing} data-testid="confirm-followup-btn">
                  {isProcessing ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
                  {hasActiveReminder ? 'Update' : 'Schedule'}
                </Button>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Restore Dialog */}
        <Dialog open={isRestoreDialogOpen} onOpenChange={setIsRestoreDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Restore Lead</DialogTitle>
              <DialogDescription>
                Are you sure you want to restore {contactForAction ? 'this lead' : `${selectedContacts.length} lead(s)`}? 
                They will be moved back to New Leads.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsRestoreDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleRestoreLead} disabled={isProcessing} data-testid="confirm-restore-btn">
                {isProcessing ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
                Restore
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-red-600">Delete Permanently</DialogTitle>
              <DialogDescription>
                Are you sure you want to permanently delete {contactForAction ? 'this lead' : `${selectedContacts.length} lead(s)`}? 
                This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleDeleteLead} disabled={isProcessing} variant="destructive" data-testid="confirm-delete-btn">
                {isProcessing ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
                Delete Permanently
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Email Composer */}
        {isEmailComposerOpen && emailContact && (
          <EmailComposer
            contact={emailContact}
            isOpen={isEmailComposerOpen}
            onClose={() => {
              setIsEmailComposerOpen(false);
              setEmailContact(null);
            }}
          />
        )}

        {/* Add Lead Dialog */}
        <Dialog open={isAddLeadDialogOpen} onOpenChange={(open) => {
          setIsAddLeadDialogOpen(open);
          if (!open && location.pathname === '/contacts/new') {
            navigate('/contacts');
          }
        }}>
          <DialogContent className="sm:max-w-md bg-white p-6">
            <DialogHeader className="pb-4">
              <DialogTitle>Add New Lead</DialogTitle>
              <DialogDescription>
                Enter the lead's contact information below.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="lead-name">Name *</Label>
                <Input
                  id="lead-name"
                  value={newLead.name}
                  onChange={(e) => setNewLead({ ...newLead, name: e.target.value })}
                  placeholder="Enter name"
                  data-testid="add-lead-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lead-email">Email</Label>
                <Input
                  id="lead-email"
                  type="email"
                  value={newLead.email}
                  onChange={(e) => setNewLead({ ...newLead, email: e.target.value })}
                  placeholder="Enter email"
                  data-testid="add-lead-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lead-phone">Phone</Label>
                <Input
                  id="lead-phone"
                  value={newLead.phone}
                  onChange={(e) => setNewLead({ ...newLead, phone: e.target.value })}
                  placeholder="Enter phone number"
                  data-testid="add-lead-phone"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lead-company">Company</Label>
                <Input
                  id="lead-company"
                  value={newLead.company}
                  onChange={(e) => setNewLead({ ...newLead, company: e.target.value })}
                  placeholder="Enter company name"
                  data-testid="add-lead-company"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lead-source">Source</Label>
                <Select value={newLead.source} onValueChange={(v) => setNewLead({ ...newLead, source: v })}>
                  <SelectTrigger data-testid="add-lead-source">
                    <SelectValue placeholder="Select source" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Website">Website</SelectItem>
                    <SelectItem value="Referral">Referral</SelectItem>
                    <SelectItem value="LinkedIn">LinkedIn</SelectItem>
                    <SelectItem value="Facebook">Facebook</SelectItem>
                    <SelectItem value="Google Ads">Google Ads</SelectItem>
                    <SelectItem value="Cold Call">Cold Call</SelectItem>
                    <SelectItem value="Email Campaign">Email Campaign</SelectItem>
                    <SelectItem value="Event">Event</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setIsAddLeadDialogOpen(false);
                if (location.pathname === '/contacts/new') {
                  navigate('/contacts');
                }
              }}>
                Cancel
              </Button>
              <Button onClick={handleAddLead} data-testid="confirm-add-lead-btn">
                <Plus size={16} className="mr-1" />
                Add Lead
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default LeadDashboard;
